import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { CurrencyDisplay } from '../components/DirhamSymbol';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Edit, Trash2, Package, Search, Download, Upload, MoreVertical, Eye } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import FileUpload from '../components/FileUpload';
import { Alert, AlertDescription } from '../components/ui/alert';
import * as XLSX from 'xlsx';
import { formatDateDisplay } from '../utils/dateUtils';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../utils/permissionUtils';
import useDeviceDetection from '../hooks/useDeviceDetection';
import { formatErrorMessage } from '../utils/formatError';

const Spares = ({ user }) => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useDeviceDetection();
  
  // Permission checks for module
  const permissions = usePermissions(user, 'spares');
  const canCreate = permissions.canCreate;
  const canEdit = permissions.canEdit;
  const canDelete = permissions.canDelete;
  
  const [spares, setSpares] = useState([]);
  const [filteredSpares, setFilteredSpares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedSpare, setSelectedSpare] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [formData, setFormData] = useState({
    part_name: '',
    part_number: '',
    model_name: '',
    manufacturer: '',
    quantity_available: 0,
    photo_url: '',
    date_of_arrival: ''
  });

  useEffect(() => {
    fetchSpares();
  }, []);

  useEffect(() => {
    // Apply search and category filter
    let filtered = spares;

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(spare =>
        spare.part_name?.toLowerCase().includes(query) ||
        spare.part_number?.toLowerCase().includes(query) ||
        spare.model_name?.toLowerCase().includes(query) ||
        spare.manufacturer?.toLowerCase().includes(query)
      );
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(spare => spare.category === categoryFilter);
    }

    setFilteredSpares(filtered);
  }, [searchQuery, spares, categoryFilter]);

  const handleViewDetails = (spare) => {
    setSelectedSpare(spare);
    setDetailDialogOpen(true);
  };

  const fetchSpares = async () => {
    try {
      const response = await axios.get(`${API}/spares`, { headers: getAuthHeader() });
      setSpares(response.data);
      setFilteredSpares(response.data);
    } catch (error) {
      toast.error('Failed to fetch spares');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/spares/${editingId}`, formData, { headers: getAuthHeader() });
        toast.success('Spare updated');
      } else {
        await axios.post(`${API}/spares`, formData, { headers: getAuthHeader() });
        toast.success('Spare added');
      }
      setDialogOpen(false);
      resetForm();
      fetchSpares();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Operation failed'));
    }
  };

  const handleEdit = (spare) => {
    setEditingId(spare.id);
    setFormData({
      part_name: spare.part_name,
      part_number: spare.part_number,
      model_name: spare.model_name,
      manufacturer: spare.manufacturer,
      quantity_available: spare.quantity_available,
      photo_url: spare.photo_url || '',
      date_of_arrival: spare.date_of_arrival
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this spare?')) {
      try {
        await axios.delete(`${API}/spares/${id}`, { headers: getAuthHeader() });
        toast.success('Spare deleted');
        fetchSpares();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      part_name: '',
      part_number: '',
      model_name: '',
      manufacturer: '',
      quantity_available: 0,
      photo_url: '',
      date_of_arrival: ''
    });
    setEditingId(null);
  };

  const downloadReport = () => {
    const exportData = spares.map(spare => ({
      'Part Name': spare.part_name,
      'Part Number': spare.part_number,
      'Model Name': spare.model_name,
      'Manufacturer': spare.manufacturer,
      'Quantity Available': spare.quantity_available,
      'Date of Arrival': spare.date_of_arrival
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Spares Inventory');
    XLSX.writeFile(workbook, `Spares_Inventory_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Report downloaded');
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/spares/template/download`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'spares_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleBulkUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/spares/bulk-upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`Upload completed: ${response.data.created} created, ${response.data.existing} already exist`);
      if (response.data.errors.length > 0) {
        console.error('Upload errors:', response.data.errors);
      }
      setBulkUploadOpen(false);
      fetchSpares();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to upload spares'));
    }
  };

  // Use permission-based access control
  const canManage = canCreate || canEdit || canDelete;
  const canDownload = permissions.canView || permissions.isAdmin;

  if (loading) return <div className="flex items-center justify-center h-96">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="spares-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-800">{t('spares.title')}</h1>
          <p className="text-slate-600 mt-1">{t('spares.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          {canDownload && (
            <Button variant="outline" onClick={downloadReport} className="gap-2">
              <Download className="w-4 h-4" />
              {t('spares.downloadReport')}
            </Button>
          )}
          {canManage && (
            <>
              <Dialog open={bulkUploadOpen} onOpenChange={setBulkUploadOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-lime-600 text-lime-600 hover:bg-lime-50">
                    <Upload className="w-4 h-4 mr-2" />
                    Bulk Upload
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Bulk Upload Spares</DialogTitle>
                    <DialogDescription>
                      Upload multiple spare parts at once using Excel file
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={handleDownloadTemplate} className="gap-2">
                        <Download className="w-4 h-4" />
                        Download Template
                      </Button>
                    </div>
                    
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-6">
                      <div className="text-center">
                        <Upload className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                        <p className="text-sm text-slate-600 mb-4">Upload Excel file with spare parts data</p>
                        <input
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleBulkUpload(file);
                            }
                          }}
                          className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-lime-50 file:text-lime-700 hover:file:bg-lime-100"
                        />
                      </div>
                    </div>

                    <Alert>
                      <AlertDescription>
                        <strong>Required columns:</strong> part_name, part_number, model_name, manufacturer, quantity_available, date_of_arrival (YYYY-MM-DD)
                      </AlertDescription>
                    </Alert>
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-lime-600 to-green-600" data-testid="add-spare-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Spare
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{editingId ? 'Edit' : 'Add'} Spare Part</DialogTitle>
                  <DialogDescription>
                    Manage spare parts inventory for maintenance
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Part Name *</Label>
                      <Input
                        value={formData.part_name}
                        onChange={(e) => setFormData({ ...formData, part_name: e.target.value })}
                        required
                        data-testid="spare-part-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Part Number *</Label>
                      <Input
                        value={formData.part_number}
                        onChange={(e) => setFormData({ ...formData, part_number: e.target.value })}
                        required
                        data-testid="spare-part-number"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Model Name *</Label>
                      <Input
                        value={formData.model_name}
                        onChange={(e) => setFormData({ ...formData, model_name: e.target.value })}
                        placeholder="Motorcycle model this fits"
                        required
                        data-testid="spare-model"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Manufacturer *</Label>
                      <Input
                        value={formData.manufacturer}
                        onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                        required
                        data-testid="spare-manufacturer"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Quantity Available *</Label>
                      <Input
                        type="number"
                        value={formData.quantity_available}
                        onChange={(e) => setFormData({ ...formData, quantity_available: parseInt(e.target.value) })}
                        required
                        data-testid="spare-quantity"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date of Arrival *</Label>
                      <Input
                        type="date"
                        value={formData.date_of_arrival}
                        onChange={(e) => setFormData({ ...formData, date_of_arrival: e.target.value })}
                        required
                        data-testid="spare-date"
                      />
                    </div>
                  </div>
                  <FileUpload
                    label="Part Photo"
                    value={formData.photo_url}
                    onChange={(url) => setFormData({ ...formData, photo_url: url })}
                    accept="image/*"
                    API={API}
                    getAuthHeader={getAuthHeader}
                  />
                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-gradient-to-r from-lime-600 to-green-600">
                      {editingId ? 'Update' : 'Add'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Spare Parts Inventory</CardTitle>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="engine">Engine</SelectItem>
                <SelectItem value="electrical">Electrical</SelectItem>
                <SelectItem value="body">Body</SelectItem>
                <SelectItem value="tire">Tire</SelectItem>
                <SelectItem value="battery">Battery</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Part Name</TableHead>
                  <TableHead>Part Number</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Manufacturer</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead>Date of Arrival</TableHead>
                  {canManage && <TableHead>Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSpares.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canManage ? 7 : 6} className="text-center text-slate-500 py-8">
                      No spare parts found. Add your first spare to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSpares.map((spare) => (
                    <TableRow 
                      key={spare.id} 
                      data-testid={`spare-row-${spare.id}`}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleViewDetails(spare)}
                    >
                      <TableCell className="font-medium">{spare.part_name}</TableCell>
                      <TableCell>{spare.part_number}</TableCell>
                      <TableCell>{spare.model_name}</TableCell>
                      <TableCell>{spare.manufacturer}</TableCell>
                      <TableCell>
                        <Badge className={spare.quantity_available > 10 ? 'bg-green-600' : spare.quantity_available > 0 ? 'bg-orange-600' : 'bg-red-600'}>
                          {spare.quantity_available} units
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDateDisplay(spare.date_of_arrival)}</TableCell>
                      {(canEdit || canDelete) && (
                        <TableCell>
                          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                            {canEdit && (
                              <Button size="sm" variant="outline" onClick={() => handleEdit(spare)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button size="sm" variant="outline" onClick={() => handleDelete(spare.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Spare Part Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Spare Part Details - {selectedSpare?.part_name}</DialogTitle>
            <DialogDescription>Complete part information</DialogDescription>
          </DialogHeader>
          {selectedSpare && (
            <div className="space-y-4">
              <Card>
                <CardHeader><CardTitle className="text-lg">Part Information</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600">Part Name</Label>
                      <p className="font-medium">{selectedSpare.part_name}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Part Number</Label>
                      <p className="font-medium">{selectedSpare.part_number}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Model</Label>
                      <p className="font-medium">{selectedSpare.model_name}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Manufacturer</Label>
                      <p className="font-medium">{selectedSpare.manufacturer || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Category</Label>
                      <Badge variant="outline">{selectedSpare.category}</Badge>
                    </div>
                    <div>
                      <Label className="text-slate-600">Quantity</Label>
                      <p className="font-medium">{selectedSpare.quantity}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Rate</Label>
                      <p className="font-bold text-green-600"><CurrencyDisplay amount={selectedSpare.rate} /></p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          <div className="flex justify-end mt-4">
            <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Spares;
